import { DefaultTheme, ThemeProvider, Theme } from "@react-navigation/native";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Stack,
  useGlobalSearchParams,
  usePathname,
  useNavigationContainerRef,
} from "expo-router";
import { StatusBar } from "expo-status-bar";
import { Platform } from "react-native";
import { StyleSheet } from "@/utils/appStyleSheet";
import { Colors } from "@/constants/Colors";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import Toast from "react-native-toast-message";
import RefundSuccessPollerProvider from "@/components/RefundSuccessPollerProvider";
import AppMaintaining from "@/components/AppMaintaining";
import AppRequireUpdate from "@/components/AppRequireUpdate";
import { toastConfig } from "@/components/ui/CustomToast";
import "react-native-reanimated";
import { useAppFont } from "@/hooks/useAppFont";
import React, { useEffect } from "react";
import * as SplashScreen from "expo-splash-screen";
import { SCREEN_WIDTH } from "@/constants/Sizes";
import useAppStore from "@/store/app";
import useRemoteConfig from "@/hooks/useRemoteConfig";
import { storageUtils } from "@/utils/storage";
import * as Crypto from "expo-crypto";
import { ROOT_ROUTES, ROOT_ROUTES_WEB } from "@/constants/Routes";
import useWindow from "@/hooks/useWindow";
import FBTrackingService from "@/services/fbTrackingService";
import useInitApp from "@/hooks/sharedHook/useInitApp";
import { APP_BASE_URL } from "@/constants/Configs";
import * as Sentry from "@sentry/react-native";

const isProduction = !__DEV__;

const navigationIntegration = Sentry.reactNavigationIntegration({
  enableTimeToInitialDisplay: true,
});

// Third-party SDK errors we can't action on
const SENTRY_EXCLUDED_ERROR_MESSAGES = [
  "com.appsflyer.sdk", // AppsFlyer attribution SDK
];

if (isProduction) {
  Sentry.init({
    dsn: "https://28e21b436ac78fb804e9c8ec9235dc2d@o4509903323398144.ingest.us.sentry.io/4510032572121089",
    environment: "production",
    sendDefaultPii: false,
    integrations: [
      Sentry.mobileReplayIntegration(),
      navigationIntegration,
      Sentry.reactNativeTracingIntegration(),
      Sentry.screenshotIntegration(),
    ],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    tracesSampleRate: 0.1,
    enableNativeCrashHandling: true,
    initialScope: {
      tags: {
        component: "mobile-app",
        framework: "react-native",
        platform: Platform.OS,
      },
    },
    beforeSend(event, hint) {
      const error = hint.originalException;

      // Check if error is a string directly (common for native SDK errors)
      if (typeof error === "string") {
        const shouldExclude = SENTRY_EXCLUDED_ERROR_MESSAGES.some((msg) =>
          error.includes(msg),
        );
        if (shouldExclude) {
          return null;
        }
      }

      // Check error.message property
      if (
        error &&
        typeof error === "object" &&
        "message" in error &&
        typeof error.message === "string"
      ) {
        const shouldExclude = SENTRY_EXCLUDED_ERROR_MESSAGES.some((msg) =>
          (error as { message: string }).message.includes(msg),
        );
        if (shouldExclude) {
          return null;
        }
      }

      // Check event.exception.values as fallback
      const exceptionValue = event.exception?.values?.[0]?.value;
      if (exceptionValue && typeof exceptionValue === "string") {
        const shouldExclude = SENTRY_EXCLUDED_ERROR_MESSAGES.some((msg) =>
          exceptionValue.includes(msg),
        );
        if (shouldExclude) {
          return null;
        }
      }

      return event;
    },
  });
}

SplashScreen.preventAutoHideAsync();

const queryClient = new QueryClient();

const AppTheme: Theme = {
  ...DefaultTheme,
  colors: {
    ...DefaultTheme.colors,
    background: Colors.background,
    card: Colors.background,
  },
};

const RootLayout: React.FC = () => {
  const glob = useGlobalSearchParams();
  const pathname = usePathname();
  const { init, isInitialized, hasSession } = useAppStore();
  const [loaded] = useAppFont();
  const { maintaining, requireUpdate } = useRemoteConfig();
  const navigationRef = useNavigationContainerRef();

  const isWeb = Platform.OS === "web";

  const { isDesktop } = useWindow();
  useInitApp();

  useEffect(() => {
    if (navigationRef) {
      navigationIntegration.registerNavigationContainer(navigationRef);
    }
  }, [navigationRef]);

  useEffect(() => {
    const deviceId = storageUtils.getDeviceId();
    if (!deviceId) {
      storageUtils.setDeviceId(Crypto.randomUUID());
    }
    setTimeout(() => {
      FBTrackingService.trackAppLaunch();
    }, 1000);
  }, []);

  useEffect(() => {
    if (loaded) {
      SplashScreen.hideAsync();
      init({
        sessionId: glob.sessionId as string,
        initData: {
          route: pathname,
          params: { ...glob, sessionId: undefined },
        },
      });
    }
  }, [loaded]);

  if (!loaded || !isInitialized) {
    // Wait for fonts AND session initialization before rendering children.
    // This prevents child routes from mounting (and their hooks from running)
    // before we know the auth state.
    return null;
  }

  // Show maintenance screen when maintaining is true
  if (maintaining) {
    return <AppMaintaining />;
  }

  // Show require update screen when requireUpdate is true
  if (!isWeb && requireUpdate) {
    return <AppRequireUpdate />;
  }

  // Auth gate for web: redirect unauthenticated users BEFORE rendering children.
  // This prevents child routes from mounting, so their hooks never run,
  // which eliminates "Session not found" API errors.
  if (isWeb && !hasSession) {
    if (typeof window !== "undefined") {
      window.location.href = APP_BASE_URL;
    }
    return null;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <GestureHandlerRootView>
        <ThemeProvider value={AppTheme}>
          <RefundSuccessPollerProvider>
            <Stack
              screenOptions={{
                contentStyle: isWeb
                  ? isDesktop
                    ? styles.containerDesktop
                    : styles.containerWeb
                  : {},
              }}
            >
              {Object.values(isWeb ? ROOT_ROUTES_WEB : ROOT_ROUTES).map(
                (route) => {
                  return (
                    <Stack.Screen
                      key={route}
                      name={route}
                      options={{ headerShown: false }}
                    />
                  );
                },
              )}
            </Stack>
            <StatusBar style="auto" />
          </RefundSuccessPollerProvider>
        </ThemeProvider>
        <Toast config={toastConfig} />
      </GestureHandlerRootView>
    </QueryClientProvider>
  );
};

export default isProduction ? Sentry.wrap(RootLayout) : RootLayout;

const styles = StyleSheet.create({
  containerWeb: {
    flex: 1,
    width: SCREEN_WIDTH,
    height: "100%",
    alignSelf: "center",
    backgroundColor: Colors.background,
  },
  containerDesktop: {
    flex: 1,
    height: "100%",
    backgroundColor: Colors.background,
  },
});
